import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import type { MasterControlUserDryRun } from "@/hooks/useMasterControl";

type DryRunDeleteUserModalProps = {
  open: boolean;
  preview: MasterControlUserDryRun | null;
  targetId: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  confirming: boolean;
};

export function DryRunDeleteUserModal({
  open,
  preview,
  targetId,
  onOpenChange,
  onConfirm,
  confirming,
}: DryRunDeleteUserModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-rose-200">Preview user deletion</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                Inspect the related records that will be impacted by removing this user.
              </Dialog.Description>
            </div>
            <button
              type="button"
              className="rounded-full border border-border p-1 text-foreground/80 hover:text-primary-foreground dark:text-amber-200"
              onClick={() => onOpenChange(false)}
            >
              ×
            </button>
          </div>

          <div className="mt-4 space-y-4 text-sm text-foreground/80">
            <div className="rounded-xl border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">User</p>
              {preview?.exists ? (
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">ID</p>
                    <p className="font-semibold">{preview.user?.id ?? targetId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p>{preview.user?.email ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Role</p>
                    <p className="capitalize">{preview.user?.role ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Organization</p>
                    <p>{preview.user?.org_id ?? "—"}</p>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-muted-foreground">No user found for {targetId}.</p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-background/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Related records</p>
              <dl className="mt-2 grid gap-4 md:grid-cols-3">
                <div>
                  <dt className="text-xs text-muted-foreground">Events</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.events ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Agent actions</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.agent_actions ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Memory vectors</dt>
                  <dd className="text-lg font-semibold text-foreground">{preview?.related.memory_vectors ?? 0}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirming || !preview?.exists}
              onClick={() => {
                void onConfirm();
              }}
            >
              {confirming ? "Deleting…" : "Confirm delete"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default DryRunDeleteUserModal;
